import { z } from 'zod'
import { GridRowSchema } from '@/types/schemas/grid-row'

// Max watchlists a single scan filter may reference. Mirrors the backend
// `MAX_WATCHLIST_FILTER_LISTS` (edge_financial_api/services/watchlist_filtering.py)
// — keep in sync. Bounds the inlined `ticker IN/NOT IN (...)` literals the
// scanner query carries (N lists × up to 250 items). The frontend uses it to
// stop the multi-select at N; the backend rejects anything over it.
export const MAX_WATCHLIST_FILTER_LISTS = 4

// ─── Watchlist Profile ───

export const WatchlistProfileSchema = z.object({
  id: z.number(),
  profileName: z.string(),
  predefined: z.boolean(),
  // Curated (Edge-managed) lists only: the cross-environment key layouts use,
  // and one line shown under the name. Null on user lists; absent until the
  // environment's database has them (Liquibase 1.55).
  slug: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
})

export type WatchlistProfile = z.infer<typeof WatchlistProfileSchema>

// ─── Watchlist Memberships (from /memberships endpoint) ───

export const WatchlistMembershipSchema = z.object({
  id: z.number(),
  name: z.string(),
  predefined: z.boolean(),
  tickers: z.array(z.string()),
})

export type WatchlistMembership = z.infer<typeof WatchlistMembershipSchema>

// ─── Add Ticker Response ───

export const WatchlistItemResponseSchema = z.object({
  id: z.number().optional(),
  row_order: z.number().optional(),
  ticker: z.string().optional(),
})

export type WatchlistItemResponse = z.infer<typeof WatchlistItemResponseSchema>

// ─── Bulk Add Response (from /{watchlistId}/items endpoint) ───

export const WatchlistBulkAddResultSchema = z.object({
  added: z.array(WatchlistItemResponseSchema),
  // Tickers not added: unknown symbol, already present, or over the list cap.
  skipped: z.array(z.string()),
  // The same tickers split by reason. Absent from servers that predate the split.
  unknown: z.array(z.string()).optional(),
  already_present: z.array(z.string()).optional(),
  over_limit: z.array(z.string()).optional(),
})

export type WatchlistBulkAddResult = z.infer<typeof WatchlistBulkAddResultSchema>

// ─── Create Watchlist Response ───

export const WatchlistCreatedResponseSchema = z.object({
  profile: WatchlistProfileSchema,
  // A COPY's rows come back without watchlist_id (the server strips it; the
  // id is on `profile`). Requiring it rejected every successful fork.
  data: z.array(GridRowSchema.partial({ watchlist_id: true })),
})

export type WatchlistCreatedResponse = z.infer<typeof WatchlistCreatedResponseSchema>
